import * as yup from "yup";

/**
 * Reusable Yup validation schema for required 11-digit phone numbers.
 */
export const phoneYupSchema = yup
  .string()
  .matches(/^\d{11}$/, "Phone number must be exactly 11 digits")
  .required("Required");

/**
 * Reusable Yup validation schema for optional 11-digit phone numbers.
 */
export const optionalPhoneYupSchema = yup
  .string()
  .test(
    "phone-format",
    "Phone number must be exactly 11 digits",
    (val) => !val || /^\d{11}$/.test(val)
  );

/**
 * Clean and format phone number input value by removing non-digits and capping at 11 digits.
 * @param {string} value
 * @returns {string}
 */
export const formatPhoneNumberInput = (value) => {
  if (!value) return "";
  return String(value).replace(/\D/g, "").slice(0, 11);
};

export const PHONE_PLACEHOLDER = "08012345678";

export const PHONE_INPUT_PROPS = {
  placeholder: PHONE_PLACEHOLDER,
  inputProps: { maxLength: 11, inputMode: "numeric" },
};

/**
 * Normalizes a phone number string to international 234 format digits (e.g., "2348012345678")
 * @param {string} phone
 * @param {string} defaultDigits
 * @returns {string}
 */
export const normalizePhoneDigits = (phone, defaultDigits = "2348088885123") => {
  if (!phone) return defaultDigits;
  const digits = String(phone).replace(/\D/g, "");
  if (!digits) return defaultDigits;

  // Handle +234080... (14 digits) by stripping the redundant leading 0
  if (digits.startsWith("2340") && digits.length === 14) {
    return `234${digits.slice(4)}`;
  }
  if (digits.startsWith("0") && digits.length === 11) {
    return `234${digits.slice(1)}`;
  }
  if (digits.length === 10) {
    return `234${digits}`;
  }
  if (digits.startsWith("234")) {
    return digits;
  }
  return digits;
};

/**
 * Formats a phone number for tel: links (e.g., "tel:+2348012345678")
 * @param {string} phone
 * @param {string} defaultPhone
 * @returns {string}
 */
export const formatTelLink = (phone, defaultPhone = "+2348088885123") => {
  if (!phone) return `tel:${defaultPhone}`;
  const digits = normalizePhoneDigits(phone, "");
  if (!digits) return `tel:${defaultPhone}`;
  return `tel:+${digits}`;
};

/**
 * Formats a phone number for WhatsApp wa.me links (e.g., "https://wa.me/2348012345678")
 * @param {string} phone
 * @param {string} defaultDigits
 * @returns {string}
 */
export const formatWhatsappLink = (phone, defaultDigits = "2348088885123") => {
  const digits = normalizePhoneDigits(phone, defaultDigits);
  return `https://wa.me/${digits}`;
};
